import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { HomeHeader } from '../home/HomeHeader'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { ChevronLeft, Upload, FileText, CheckCircle2, XCircle, Loader2, X, Eye } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { BOOK_FILE_FORMATS, isValidBookFile } from '../../lib/constants'

type UploadResult = {
  book_id: string
  filename: string
  title: string
  author: string
  estimated_pages: number | null
}

type FailedUpload = {
  filename: string
  error: string
}

export function AdminMultiUpload() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [results, setResults] = useState<{
    success: UploadResult[]
    failed: FailedUpload[]
  } | null>(null)

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    
    const files = Array.from(e.dataTransfer.files).filter(isValidBookFile)
    if (files.length > 0) {
      setSelectedFiles(prev => [...prev, ...files])
    }
  }, [])

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (files) {
      const validFiles = Array.from(files).filter(isValidBookFile)
      setSelectedFiles(prev => [...prev, ...validFiles])
    }
    // Reset input so same file can be selected again
    e.target.value = ''
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const clearFiles = () => {
    setSelectedFiles([])
  }

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return
    
    setUploading(true)
    try {
      const result = await apiClient.admin.bulkUploadBooks(selectedFiles)
      setResults({
        success: result.results,
        failed: result.failed
      })
      setSelectedFiles([])
    } catch (error) {
      console.error('Upload failed:', error)
      alert('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="admin-multi-upload min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/management')}
            className="back-link flex items-center text-muted-foreground hover:text-foreground mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Books
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-primary rounded-full"></div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Bulk Upload Books</h1>
              <p className="text-sm text-muted-foreground">
                Upload multiple books at once. Books will be saved as hidden for review.
              </p>
            </div>
          </div>
        </div>

        {/* Upload Zone */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Files</CardTitle>
            <CardDescription>
              Drag and drop book files or click to select. Supported: {BOOK_FILE_FORMATS.displayString}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              className={`upload-zone border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                isDragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true) }}
              onDragLeave={() => setIsDragging(false)}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <input
                type="file"
                multiple
                accept={BOOK_FILE_FORMATS.acceptString}
                onChange={handleFileSelect}
                className="hidden"
                id="file-input"
              />
              <span className="text-primary hover:underline font-medium">
                {isDragging ? 'Drop files here' : 'Drag and drop or click to select files'}
              </span>
              <p className="text-xs text-muted-foreground mt-2">
                You can select multiple files at once
              </p>
            </div>

            {/* Selected Files List */}
            {selectedFiles.length > 0 && (
              <div className="selected-files mt-4 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">{selectedFiles.length} file(s) selected</p>
                  <Button variant="ghost" size="sm" onClick={clearFiles}>
                    Clear all
                  </Button>
                </div>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="file-item flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); removeFile(index) }}
                        className="flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
                
                <Button 
                  onClick={handleUpload} 
                  disabled={uploading}
                  className="w-full mt-4"
                  size="lg"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading & Analyzing...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload {selectedFiles.length} Book{selectedFiles.length > 1 ? 's' : ''}
                    </>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results */}
        {results && (
          <Card>
            <CardHeader>
              <CardTitle>Upload Results</CardTitle>
              <CardDescription>
                {results.success.length} succeeded, {results.failed.length} failed
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Successful uploads */}
              {results.success.length > 0 && (
                <div className="success-results">
                  <h4 className="font-medium text-green-600 dark:text-green-400 mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5" />
                    Successfully Uploaded ({results.success.length})
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    These books are now hidden. Edit each book to set genre and make it visible.
                  </p>
                  <div className="space-y-2">
                    {results.success.map((book) => (
                      <div key={book.book_id} className="result-item p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                        <div className="flex justify-between items-start gap-4">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate">{book.title}</p>
                            <p className="text-sm text-muted-foreground">by {book.author}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              {book.estimated_pages && (
                                <span>~{book.estimated_pages} pages</span>
                              )}
                              <span className="text-orange-600 dark:text-orange-400 flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                Hidden
                              </span>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/admin/books/${book.book_id}/edit`)}
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Failed uploads */}
              {results.failed.length > 0 && (
                <div className="failed-results">
                  <h4 className="font-medium text-red-600 dark:text-red-400 mb-3 flex items-center gap-2">
                    <XCircle className="h-5 w-5" />
                    Failed ({results.failed.length})
                  </h4>
                  <div className="space-y-2">
                    {results.failed.map((item, index) => (
                      <div key={index} className="result-item p-4 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                        <p className="font-medium truncate">{item.filename}</p>
                        <p className="text-sm text-red-600 dark:text-red-400 mt-1">{item.error}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  variant="outline"
                  onClick={() => setResults(null)}
                >
                  Upload More
                </Button>
                <Button 
                  onClick={() => navigate('/admin/management?include_hidden=true')}
                >
                  View All Books
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info */}
        {!results && selectedFiles.length === 0 && (
          <Card className="bg-muted/30">
            <CardContent className="pt-6">
              <h4 className="font-medium mb-2">What happens when you upload:</h4>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Books are automatically analyzed to extract title, author, description, and cover</li>
                <li>• Language is auto-detected from the content</li>
                <li>• Page count is estimated</li>
                <li>• Books are saved as <span className="text-orange-600 dark:text-orange-400 font-medium">hidden</span> for review</li>
                <li>• Genre is left empty — you'll need to set it manually when editing</li>
              </ul>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

